import { AxiosInstance } from 'axios';

import {
  IWorkflowData,
  IWorkflowStateData,
  IWorkflowTransitionData,
  IWorkflowStateThresholdData,
  IWorkflowTransitionActionData,
  IWorkflowStateThresholdGroupData
} from '@/models/workflow';
import { IWorkflowService } from './workflow.types';
import { IUserData } from '@/models';

export class WorkflowService implements IWorkflowService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/workflows/';
  }

  fetchWorkflows(): Promise<IWorkflowData[]> {
    return this.axios
      .get<IWorkflowData[]>(`${this.endpoint}`)
      .then(({ data }) => data);
  }

  fetchWorkflow(id: number): Promise<IWorkflowData> {
    return this.axios
      .get<IWorkflowData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createWorkflow(workflow: IWorkflowData): Promise<IWorkflowData> {
    return this.axios
      .post<IWorkflowData>(`${this.endpoint}`, workflow)
      .then(({ data }) => data);
  }

  updateWorkflow(workflow: IWorkflowData): Promise<IWorkflowData> {
    return this.axios
      .patch<IWorkflowData>(`${this.endpoint}${workflow.id}/`, workflow)
      .then(({ data }) => data);
  }

  deleteWorkflow(id: number): Promise<void> {
    return this.axios.delete(`${this.endpoint}${id}/`).then(({ data }) => data);
  }

  setWorkflowState(id: number, state: number): Promise<IWorkflowData> {
    return this.axios
      .put<IWorkflowData>(`${this.endpoint}${id}/set-state/${state}/`)
      .then(({ data }) => data);
  }

  /* #### State #### */

  fetchWorkflowStates(id: number): Promise<IWorkflowStateData[]> {
    return this.axios
      .get<IWorkflowStateData[]>(`${this.endpoint}${id}/states/`)
      .then(({ data }) => data);
  }

  fetchWorkflowState(id: number, itemId: number): Promise<IWorkflowStateData> {
    return this.axios
      .get<IWorkflowStateData>(`${this.endpoint}${id}/states/${itemId}/`)
      .then(({ data }) => data);
  }

  createWorkflowState(
    id: number,
    data: IWorkflowStateData
  ): Promise<IWorkflowStateData> {
    return this.axios
      .post<IWorkflowStateData>(`${this.endpoint}${id}/states/`, data)
      .then(({ data }) => data);
  }

  updateWorkflowState(
    id: number,
    data: IWorkflowStateData
  ): Promise<IWorkflowStateData> {
    return this.axios
      .patch<IWorkflowStateData>(
        `${this.endpoint}${id}/states/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowState(id: number, itemId: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${id}/states/${itemId}/`)
      .then(({ data }) => data);
  }

  /* #### Transition #### */
  fetchWorkflowTransitions(id: number): Promise<IWorkflowTransitionData[]> {
    return this.axios
      .get<IWorkflowTransitionData[]>(`${this.endpoint}${id}/transitions/`)
      .then(({ data }) => data);
  }

  fetchWorkflowTransition(
    id: number,
    itemId: number
  ): Promise<IWorkflowTransitionData> {
    return this.axios
      .get<IWorkflowTransitionData>(
        `${this.endpoint}${id}/transitions/${itemId}/`
      )
      .then(({ data }) => data);
  }

  createWorkflowTransition(
    id: number,
    data: IWorkflowTransitionData
  ): Promise<IWorkflowTransitionData> {
    return this.axios
      .post<IWorkflowTransitionData>(`${this.endpoint}${id}/transitions/`, data)
      .then(({ data }) => data);
  }

  updateWorkflowTransition(
    id: number,
    data: IWorkflowTransitionData
  ): Promise<IWorkflowTransitionData> {
    return this.axios
      .patch<IWorkflowTransitionData>(
        `${this.endpoint}${id}/transitions/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowTransition(id: number, itemId: number): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${id}/transitions/${itemId}/`)
      .then(({ data }) => data);
  }

  /* #### Workflow State Threshold Group #### */
  fetchWorkflowStateThresholdGroups(
    workflowId: number,
    stateId: number
  ): Promise<IWorkflowStateThresholdGroupData[]> {
    return this.axios
      .get<IWorkflowStateThresholdGroupData[]>(
        `${this.endpoint}${workflowId}/states/${stateId}/threshold-groups/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowStateThresholdGroup(
    workflowId: number,
    stateId: number,
    groupId: number
  ): Promise<IWorkflowStateThresholdGroupData> {
    return this.axios
      .get<IWorkflowStateThresholdGroupData>(
        `${this.endpoint}${workflowId}/states/${stateId}/threshold-groups/${groupId}/`
      )
      .then(({ data }) => data);
  }
  createWorkflowStateThresholdGroup(
    workflowId: number,
    stateId: number,
    data: IWorkflowStateThresholdGroupData
  ): Promise<IWorkflowStateThresholdGroupData> {
    return this.axios
      .post<IWorkflowStateThresholdGroupData>(
        `${this.endpoint}${workflowId}/states/${stateId}/threshold-groups/`,
        data
      )
      .then(({ data }) => data);
  }
  updateWorkflowStateThresholdGroup(
    workflowId: number,
    stateId: number,
    data: IWorkflowStateThresholdGroupData
  ): Promise<IWorkflowStateThresholdGroupData> {
    return this.axios
      .patch<IWorkflowStateThresholdGroupData>(
        `${this.endpoint}${workflowId}/states/${stateId}/threshold-groups/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }
  deleteWorkflowStateThresholdGroup(
    workflowId: number,
    stateId: number,
    groupId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${workflowId}/states/${stateId}/threshold-groups/${groupId}/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowStateThresholds(
    workflowId: number,
    stateId: number
  ): Promise<IWorkflowStateThresholdData[]> {
    return this.axios
      .get<IWorkflowStateThresholdData[]>(
        `${this.endpoint}${workflowId}/states/${stateId}/thresholds/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowStateThreshold(
    workflowId: number,
    stateId: number,
    thresholdId: number
  ): Promise<IWorkflowStateThresholdData> {
    return this.axios
      .get<IWorkflowStateThresholdData>(
        `${this.endpoint}${workflowId}/states/${stateId}/thresholds/${thresholdId}/`
      )
      .then(({ data }) => data);
  }

  createWorkflowStateThreshold(
    workflowId: number,
    stateId: number,
    data: IWorkflowStateThresholdData
  ): Promise<IWorkflowStateThresholdData> {
    return this.axios
      .post<IWorkflowStateThresholdData>(
        `${this.endpoint}${workflowId}/states/${stateId}/thresholds/`,
        data
      )
      .then(({ data }) => data);
  }

  updateWorkflowStateThreshold(
    workflowId: number,
    stateId: number,
    data: IWorkflowStateThresholdData
  ): Promise<IWorkflowStateThresholdData> {
    return this.axios
      .patch<IWorkflowStateThresholdData>(
        `${this.endpoint}${workflowId}/states/${stateId}/thresholds/${data.id}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowStateThreshold(
    workflowId: number,
    stateId: number,
    thresholdId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${workflowId}/states/${stateId}/thresholds/${thresholdId}/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowTransitionActions(
    workflowId: number,
    transitionId: number
  ): Promise<IWorkflowTransitionActionData[]> {
    return this.axios
      .get<IWorkflowTransitionActionData[]>(
        `${this.endpoint}${workflowId}/transitions/${transitionId}/actions/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowTransitionAction(
    workflowId: number,
    transitionId: number,
    actionId: number
  ): Promise<IWorkflowTransitionActionData> {
    return this.axios
      .get<IWorkflowTransitionActionData>(
        `${this.endpoint}${workflowId}/transitions/${transitionId}/actions/${actionId}/`
      )
      .then(({ data }) => data);
  }

  createWorkflowTransitionAction(
    workflowId: number,
    transitionId: number,
    data: IWorkflowTransitionActionData
  ): Promise<IWorkflowTransitionActionData> {
    return this.axios
      .post<IWorkflowTransitionActionData>(
        `${this.endpoint}${workflowId}/transitions/${transitionId}/actions/`,
        data
      )
      .then(({ data }) => data);
  }

  updateWorkflowTransitionAction(
    workflowId: number,
    transitionId: number,
    data: IWorkflowTransitionActionData
  ): Promise<IWorkflowTransitionActionData> {
    return this.axios
      .patch<IWorkflowTransitionActionData>(
        `${
          this.endpoint
        }${workflowId}/transitions/${transitionId}/actions/${data.id!}/`,
        data
      )
      .then(({ data }) => data);
  }

  deleteWorkflowTransitionAction(
    workflowId: number,
    transitionId: number,
    actionId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${workflowId}/transitions/${transitionId}/actions/${actionId}/`
      )
      .then(({ data }) => data);
  }

  fetchWorkflowTransitionActionsEmailRecipients(
    workflowId: number,
    transitionId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get<IUserData[]>(
        `${this.endpoint}${workflowId}/transitions/${transitionId}/actions/get-email-recipients/`
      )
      .then(({ data }) => data);
  }
}
